import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { GitMerge, Plus, ChevronRight } from 'lucide-react'
import { listFunnelConnectors, getFunnelRuns } from '../api/funnel'
import { Button } from '../components/ui/button'
import { SearchInput } from '../components/SearchInput'

const TYPE_COLORS: Record<string, string> = {
  mysql:      'bg-[#00758f]/10 text-[#00758f] border-[#00758f]/20',
  postgresql: 'bg-[#336791]/10 text-[#336791] border-[#336791]/20',
}
function typeBadge(t: string) {
  const cls = TYPE_COLORS[t] ?? 'bg-paper-sunken text-ink-dim border-paper-border'
  return <span className={`px-1.5 py-px text-[10px] font-medium rounded border ${cls}`}>{t}</span>
}
import { EmptyState } from '../components/EmptyState'
import { useDateFmt } from '../hooks/useDateFmt'
import type { FunnelConnector, FunnelRun } from '../types'

function statusBadge(status: FunnelRun['status']) {
  const map = {
    running:   'bg-blue-50 text-blue-600 border-blue-100',
    completed: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    failed:    'bg-red-50 text-red-600 border-red-100',
  }
  return (
    <span className={`px-1.5 py-0.5 text-theme-xs font-medium rounded border ${map[status]}`}>
      {status}
    </span>
  )
}

function ConnectorRow({ connector }: { connector: FunnelConnector }) {
  const fmt = useDateFmt()
  const navigate = useNavigate()

  const { data: runs = [] } = useQuery({
    queryKey: ['funnel-runs', connector.id],
    queryFn: () => getFunnelRuns(connector.id).then(r => r.data),
  })

  const lastRun = (runs as FunnelRun[])[0]

  return (
    <tr
      className="cursor-pointer"
      onClick={() => navigate(`/brainer/funnels/${connector.id}`)}
    >
      <td>
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{connector.title}</span>
          {typeBadge(connector.connectorType)}
        </div>
        <span className="text-ink-dim text-xs font-mono">
          {connector.host}:{connector.port}/{connector.databaseName}
        </span>
      </td>
      <td className="text-ink-dim text-xs">{connector.username}</td>
      <td>
        {lastRun ? statusBadge(lastRun.status) : <span className="text-ink-dim text-xs">no runs</span>}
      </td>
      <td className="text-ink-dim text-xs">
        {lastRun ? fmt(lastRun.startedAt) : '—'}
      </td>
      <td>
        <div className="flex items-center justify-end">
          <ChevronRight size={13} className="text-ink-dim" />
        </div>
      </td>
    </tr>
  )
}

export function BrainerFunnels() {
  const navigate = useNavigate()

  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ['funnel-connectors'],
    queryFn: () => listFunnelConnectors().then(r => r.data),
  })

  const [search, setSearch] = useState('')
  const filtered = (connectors as FunnelConnector[]).filter(c =>
    [c.title, c.host, c.databaseName, c.username].some(v => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Funnel Connectors</h1>
          <p className="text-sm text-ink-dim mt-0.5">MySQL databases connected to Brainer ingestion</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search connectors" />
          <Button icon={Plus} onClick={() => navigate('/brainer/funnels/catalog')}>New connector</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading…</div>
        ) : (connectors as FunnelConnector[]).length === 0 ? (
          <EmptyState icon={GitMerge} title="No connectors yet" description="Create a connector to start ingesting MySQL data into Brainer" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={GitMerge} title="No matching connectors" description="Try a different search" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Connector</th>
                <th>User</th>
                <th>Last status</th>
                <th>Last run</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <ConnectorRow key={c.id} connector={c} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
